import type { FC } from 'react';
import { useEffect, useMemo } from 'react';

import { Grid, Box, useDeviceType, useRouterTransition, useMedia } from '@packages/shared';
import type { GenericTrackingEvent } from '@packages/tracking';
import { useTracking } from '@packages/tracking';
import { useConfig } from '@packages/utilities';

import type { DesktopDisplayMode } from '../../types';
import type {
  CriteoResponse,
  CurrentNavigation,
  SelectedFilter,
  SortingOrder,
} from '../../api/rest/types';

import type { SearchProductCardProps } from '../SearchProductCard';
import { SearchProductCard } from '../SearchProductCard';
import { CriteoProductCard } from '../CriteoProductCard';
import { CriteoBeaconImage } from '../CriteoBeaconImage';
import { ProductGridSkeleton } from '../ProductGridSkeleton';
import { ShimmeringBlurOverlay } from '../ShimmeringBlurOverlay';

import { getGridColumns } from './getGridColumns';
import { getCardSize } from './getCardSize';
import { useProductsData } from './useProductsData';
import { useProductListTracking } from './useProductListTracking';

export type ProductGridProps = {
  pageTitle: string;
  count: number;
  sortType: string;
  selectedFilters: SelectedFilter[];
  alternativeQuery?: string;
  resultFallbackType?: 'DeepQuery' | 'QueryReduction';
  pageType: 'serp' | 'productlist';
  /**
   * Top level categories in the nav tree
   */
  topLevelCategories?: string[];
  /** product card layout
   *
   * @default 'cards'
   */
  displayMode?: DesktopDisplayMode;
  /**
   * optional page number
   */
  pageNumber?: number;
  /**
   * Event fired when any card in the grid first becomes visible
   */
  onFirstBecameVisible?: (index?: number) => void;
  /* object for sponsored products */
  criteoData?: {
    /* posible sponsored products response */
    criteoResponse: CriteoResponse | undefined;

    /* search result props required for criteo tracking */
    criteoTrackingProps: {
      category?: string;
      current?: CurrentNavigation;
      orders?: SortingOrder[];
      query?: string;
    };
  };
} & Pick<SearchProductCardProps, 'categoryDataForTracking'>;

const replaceSpecialCharacters = (text: string) =>
  text
    ?.replace(/ /g, '_')
    .replace(/&/g, '%26')
    .replace(/ä/g, 'ae')
    .replace(/Ä/g, 'ae')
    .replace(/ü/g, 'ue')
    .replace(/Ü/g, 'ue')
    .replace(/ö/g, 'oe')
    .replace(/Ö/g, 'oe');

const ProductGridBlurOverlay: FC<Pick<ProductGridProps, 'topLevelCategories'>> = ({
  topLevelCategories,
}) => {
  const {
    i18n: { languages, defaultLocale },
  } = useConfig();

  const nonDefaultLanguages = languages.filter((language) => !defaultLocale.startsWith(language));
  const productListPrefixes = nonDefaultLanguages
    .map((language) => `/${language}/s/`)
    .concat(['/s/'])
    .concat(topLevelCategories?.map((route) => `/${route}`) ?? []);

  const { isTransitioning } = useRouterTransition((url) =>
    productListPrefixes.some((prefix) => url.startsWith(prefix)),
  );

  return <ShimmeringBlurOverlay show={isTransitioning} />;
};

/**
 * ProductGrid component based on https://mui.com/material-ui/react-grid/
 * */
export const ProductGrid: FC<ProductGridProps> = ({
  categoryDataForTracking,
  topLevelCategories,
  displayMode = 'cards',
  pageNumber,
  criteoData,
  onFirstBecameVisible,
  selectedFilters,
  sortType,
  count,
  pageTitle,
  alternativeQuery,
  resultFallbackType,
  pageType,
}) => {
  const { products, isLoading } = useProductsData(categoryDataForTracking, pageNumber);
  const { isMobile, isTablet } = useDeviceType();

  const { criteoResponse, criteoTrackingProps } = criteoData || {};
  const prerenderElements = useMedia({
    mobile: criteoResponse?.criteoProducts ? 2 : 4,
    tablet: criteoResponse?.criteoProducts ? 3 : 6,
    desktop: criteoResponse?.criteoProducts ? 4 : 8,
  });

  const { orders, query, category, current } = criteoTrackingProps || {};
  const criteoProducts = criteoResponse?.criteoProducts ?? [];

  const dispatchGTMEvent = useTracking();
  const dispatchProductListTracking = useProductListTracking();

  useMemo(() => {
    if (criteoResponse) {
      // Try-Catch Block for tracking Criteo Object
      try {
        // Prepare SKU from 16 Products for Criteo Tracking
        const organicSku = products
          ?.map((item, index) => index <= 15 && item.product.variations[0].sku)
          .filter((item) => item)
          .join('|');
        // Prepare sort value for tracking
        const sortName = orders ? orders.find((item) => item.isSelected)?.displayName : '';

        dispatchGTMEvent<
          GenericTrackingEvent & {
            'organic-skus': string;
            sort: string;
            EmmaCriteoCategoryName?: string;
          }
        >({
          event: 'gtmCriteoSponsored',
          eventValue: `Begriff=${query || category || ''}`,
          'organic-skus': organicSku || '',
          sort: sortName || '',
          EmmaCriteoCategoryName:
            (current && replaceSpecialCharacters(current.name.toLowerCase())) || undefined,
        });
      } catch (e) {
        // error
      }
    }
  }, [criteoResponse, category, current, orders, products, query, dispatchGTMEvent]);

  useEffect(() => {
    if (!isLoading) {
      dispatchProductListTracking({
        categoryDataForTracking,
        count,
        pageNumber,
        pageTitle,
        pageType,
        products,
        selectedFilters,
        sortType,
        alternativeQuery,
        criteoProducts,
        displayMode,
        query,
        resultFallbackType,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading, products]);

  // eslint-disable-next-line no-nested-ternary
  const currentBreakpoint = isMobile ? 'xs' : isTablet ? 'md' : 'lg';

  if (isLoading) {
    return (
      <ProductGridSkeleton
        displayMode={displayMode}
        count={products.length > 0 ? products.length : 72}
      />
    );
  }

  const columns = getGridColumns(displayMode);

  const hasAdditionalFlags = products.some((product) => product.product.additionalFlags);
  const hasRatings = products.some((product) => product.product.avgRating);
  const hasUnitPrices = products.some((product) => product.product.variations[0].pricePerUnit);

  return (
    <Box position="relative">
      <ProductGridBlurOverlay topLevelCategories={topLevelCategories} />
      {criteoResponse?.beacons?.OnLoadBeacon && (
        <CriteoBeaconImage src={criteoResponse.beacons.OnLoadBeacon} />
      )}
      {criteoResponse?.beacons?.OnViewBeacon && (
        <CriteoBeaconImage src={criteoResponse.beacons.OnViewBeacon} />
      )}
      {criteoResponse && (
        <Grid
          container
          component="ul"
          spacing={{ xs: 2, md: 3 }}
          columns={columns}
          sx={{
            marginBottom: '2rem',
            listStyle: 'none',
            paddingLeft: 0,
          }}
        >
          {criteoResponse.beacons && (
            <Grid item position="absolute" component="li" aria-hidden key="criteo-beacons" />
          )}
        </Grid>
      )}
      <Grid
        container
        spacing={{ xs: 1.5, md: 2 }}
        columns={columns}
        component="ul"
        sx={{
          listStyle: 'none',
          paddingLeft: 0,
        }}
      >
        {/* always show exactly one full row of criteo products */}
        {criteoProducts.slice(0, columns[currentBreakpoint]).map((criteoProduct, index) => (
          <Grid
            item
            component="li"
            // eslint-disable-next-line react/no-array-index-key
            key={`criteo_${criteoProduct.masterSku}_${index}`}
            {...getCardSize(columns, displayMode, criteoProduct.isHighlight)}
          >
            <CriteoProductCard
              product={{
                ...criteoProduct,
                sellingPoints:
                  displayMode === 'moreInformation' ? criteoProduct.sellingPoints ?? [] : undefined,
              }}
            />
          </Grid>
        ))}
        {products?.map((productProps, index) => (
          <Grid
            item
            component="li"
            key={productProps.product.masterSku}
            {...getCardSize(
              columns,
              displayMode,
              (productProps.product.highlight && index === 0) ?? false,
            )}
          >
            <SearchProductCard
              product={{
                ...productProps.product,
                additionalInfo:
                  displayMode === 'moreInformation'
                    ? productProps.product.additionalInfo ?? []
                    : undefined,
              }}
              categoryDataForTracking={categoryDataForTracking}
              priority={index < columns[currentBreakpoint]}
              onFirstBecameVisible={() => onFirstBecameVisible?.(index)}
              useAdditionalFlagsSpacer={hasAdditionalFlags}
              useRatingsSpacer={hasRatings}
              useUnitPriceSpacer={hasUnitPrices}
              requestCacheKey={productProps.requestCacheKey}
              alwaysRender={index < +prerenderElements}
              displayMode={displayMode}
            />
          </Grid>
        ))}
      </Grid>
    </Box>
  );
};
